import { useLayoutEffect } from 'react';
import type { SeoConfig } from './seo.type';

/**
 * SEO 메타데이터 컴포넌트.
 *
 * React 19부터 <title>, <meta>, <link> 태그를 컴포넌트 트리 어디서든
 * 렌더하면 자동으로 <head>로 호이스팅된다.
 * 따라서 react-helmet / react-helmet-async 같은 외부 의존성이 필요 없다.
 *
 * 사용:
 *   <SEO {...buildSeo({ title: '...', description: '...' })} />
 *
 * 또는 라우트 element wrapper로:
 *   { element: <SeoRoute config={...}><ProductPage /></SeoRoute> }
 *
 * 참고:
 *  - SPA 환경이므로 크롤러(특히 SNS 미리보기 봇)는 JS 실행 후 메타를 못 읽는
 *    경우가 있다. 운영에서 OG/Twitter 카드가 중요하면 prerender (vite-ssg,
 *    react-snap) 또는 EKS 앞단에 prerender.io 같은 미들웨어를 두는 것이
 *    실무적이다. 이 컴포넌트는 그 단계 전까지의 표준 적용을 담당한다.
 */
export function SEO({
  title,
  titleTemplate,
  description,
  keywords,
  canonical,
  robots = 'index,follow',
  og,
  twitter,
  jsonLd,
}: SeoConfig) {
  const resolvedTitle = titleTemplate ? titleTemplate.replace('%s', title) : title;

  // index.html 의 정적 기본값(봇용)과 겹치면 같은 키가 두 벌이 되므로 페이지 메타가 뜨면 지운다.
  useLayoutEffect(() => {
    document.head.querySelectorAll('[data-seo-default]').forEach((el) => el.remove());
  }, []);

  return (
    <>
      <title>{resolvedTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords && keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(', ')} />
      )}
      <meta name="robots" content={robots} />
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph */}
      <meta property="og:title" content={og?.title ?? resolvedTitle} />
      {(og?.description ?? description) && (
        <meta property="og:description" content={og?.description ?? description!} />
      )}
      <meta property="og:type" content={og?.type ?? 'website'} />
      {og?.url && <meta property="og:url" content={og.url} />}
      {og?.image && <meta property="og:image" content={og.image} />}
      {og?.siteName && <meta property="og:site_name" content={og.siteName} />}
      <meta property="og:locale" content={og?.locale ?? 'ko_KR'} />

      {/* Twitter Card */}
      <meta name="twitter:card" content={twitter?.card ?? 'summary_large_image'} />
      <meta name="twitter:title" content={twitter?.title ?? resolvedTitle} />
      {(twitter?.description ?? description) && (
        <meta name="twitter:description" content={twitter?.description ?? description!} />
      )}
      {(() => {
        // optional chain 끝에 ! 를 붙이는 건 unsafe (둘 다 undefined 일 수 있음).
        // 위 조건절이 truthy 임을 IIFE 로 좁혀서 non-null assertion 없이 처리.
        const twitterImage = twitter?.image ?? og?.image;
        return twitterImage ? (
          <meta name="twitter:image" content={twitterImage} />
        ) : null;
      })()}

      {/* JSON-LD 구조화 데이터 */}
      {jsonLd && (
        <script
          type="application/ld+json"
          // React 19 native metadata는 script 호이스팅도 처리.
          // dangerouslySetInnerHTML는 JSON-LD 표준 사용 방식.
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}
    </>
  );
}
